import {
  formatCustomerMessage,
  getReasonCode,
  isReasonCode,
  PAYMENT_INTENT_TIMINGS,
} from '@paguz/contracts';
import { Badge, Button, cn, MoneyText, toast } from '@paguz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  CheckCheckIcon,
  CircleSlashIcon,
  PrinterIcon,
  RadarIcon,
  RotateCcwIcon,
  Undo2Icon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { AdminPaymentIntentDetail, SagaStepDetail } from '../../api/admin-schemas.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import { ConfirmActionDialog, SummaryRows } from '../../components/confirm-dialog.tsx';
import {
  CopyValue,
  DataList,
  ErrorState,
  LoadingRows,
  PageHeader,
  Panel,
} from '../../components/page.tsx';
import { formatDateTime, formatRate } from '../../lib/format.ts';
import { CHANNEL_LABEL, FUNDING_MODE, PAYMENT_STATUS, SAGA_STEP } from '../../lib/labels.ts';
import { useCan } from '../../lib/session.ts';
import { operatorActions, type OperatorActionKind } from './operator-actions.ts';
import { SagaTimeline } from './saga-timeline.tsx';

const route = getRouteApi('/app/transacciones/$paymentIntentId');

type PendingAction = { kind: OperatorActionKind; step?: SagaStepDetail } | null;

export function TransactionPage() {
  const { paymentIntentId } = route.useParams();
  const query = useQuery({
    queryKey: queryKeys.paymentIntent(paymentIntentId),
    queryFn: () => api.getPaymentIntent(paymentIntentId),
  });

  return (
    <>
      <Link
        to="/transacciones"
        className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
      >
        <ArrowLeftIcon className="size-4" /> Transacciones
      </Link>
      {query.isPending ? (
        <LoadingRows rows={8} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <Detail detail={query.data} />
      )}
    </>
  );
}

function Detail({ detail }: { detail: AdminPaymentIntentDetail }) {
  const { intent } = detail;
  const queryClient = useQueryClient();
  const canOperate = useCan('transactions:operate');
  const [pending, setPending] = useState<PendingAction>(null);
  const actions = canOperate ? operatorActions(detail) : [];
  const retryable = new Set(actions.filter((a) => a.kind === 'retry').map((a) => a.stepId ?? ''));
  const action = (kind: OperatorActionKind) => actions.find((a) => a.kind === kind);

  const mutation = useMutation({
    mutationFn: ({
      kind,
      reason,
      stepId,
    }: {
      kind: OperatorActionKind;
      reason: string;
      stepId?: string;
    }) => api.operatorAction(intent.id, kind, reason, stepId),
    onSuccess: (result) => {
      if (result.detail)
        queryClient.setQueryData(queryKeys.paymentIntent(intent.id), result.detail);
      else void queryClient.invalidateQueries({ queryKey: queryKeys.paymentIntent(intent.id) });
      void queryClient.invalidateQueries({ queryKey: ['payment-intents'] });
      toast.success(result.message);
    },
  });

  const status = PAYMENT_STATUS[intent.status];
  const reason =
    intent.reasonCode && isReasonCode(intent.reasonCode) ? getReasonCode(intent.reasonCode) : null;
  const queryBreb = action('query-breb');
  const force = action('force-compensation');
  const resolve = action('resolve');

  return (
    <>
      <PageHeader
        eyebrow={
          <span className="flex flex-wrap items-center gap-2">
            Intención de pago <CopyValue value={intent.id} label="ID de la intención" />
          </span>
        }
        title={intent.payee.name}
        description={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>
              {detail.customer.fullName} · {formatDateTime(intent.createdAt)}
            </span>
            <span>Canal: {CHANNEL_LABEL[intent.channel]}</span>
          </span>
        }
        actions={
          canOperate && (
            <>
              {queryBreb && (
                <Button
                  variant="highlight"
                  onClick={() => {
                    setPending({ kind: 'query-breb' });
                  }}
                >
                  <RadarIcon /> Consultar estado en Bre-B
                </Button>
              )}
              {force && (
                <Button
                  variant="destructive"
                  disabled={!force.enabled}
                  title={force.disabledReason}
                  onClick={() => {
                    setPending({ kind: 'force-compensation' });
                  }}
                >
                  <Undo2Icon /> Forzar compensación
                </Button>
              )}
              {resolve && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setPending({ kind: 'resolve' });
                  }}
                >
                  <CheckCheckIcon /> Marcar resuelta
                </Button>
              )}
            </>
          )
        }
      />

      <div className="mb-6 grid gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,2fr)]">
        <div className="rounded-2xl bg-inverse p-6 text-inverse-foreground">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm text-inverse-muted-foreground">Monto de la compra</p>
            <Badge variant={status.tone}>{status.label}</Badge>
          </div>
          <MoneyText value={intent.amount.amount} size="xl" tone="inverse" className="mt-2 block" />
          <p className="mt-3 text-sm text-inverse-foreground/85">
            {intent.installments === 1 ? '1 cuota' : `${intent.installments} cuotas`} de{' '}
            <MoneyText
              value={intent.simulation.installmentAmount.amount}
              size="sm"
              tone="inverse"
            />{' '}
            · {formatRate(intent.simulation.rateEA)}
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-white/10 px-2.5 py-1 font-semibold">
              Fondeo: {FUNDING_MODE[intent.fundingMode].label}
            </span>
            <span className="rounded-full bg-white/10 px-2.5 py-1 font-semibold">
              Posteo: {POSTING_LABEL[intent.postingStatus]}
            </span>
            {intent.qrReference && (
              <span className="rounded-full bg-white/10 px-2.5 py-1 font-semibold">
                QR {intent.qrReference}
              </span>
            )}
          </div>
        </div>
        <Panel bodyClassName="p-5">
          <DataList
            columns={3}
            items={[
              {
                label: 'Cliente',
                value: (
                  <Link
                    to="/clientes/$customerId"
                    params={{ customerId: detail.customer.id }}
                    className="font-semibold text-primary hover:underline"
                  >
                    {detail.customer.fullName}
                  </Link>
                ),
                hint: detail.customer.maskedDocument,
              },
              {
                label: 'Línea',
                value: (
                  <Link
                    to="/lineas/$lineId"
                    params={{ lineId: intent.lineId }}
                    search={{}}
                    className="font-semibold text-primary hover:underline"
                  >
                    Ver línea y cronograma
                  </Link>
                ),
              },
              {
                label: 'Comercio',
                value: intent.payee.name,
                hint: `${intent.payee.aliasValue} · ${intent.payee.participantName}`,
              },
              {
                label: 'Autorización (authorizationId)',
                value: <CopyValue value={detail.authorizationId} label="authorizationId" />,
              },
              {
                label: 'Utilización (drawdownId)',
                value: <CopyValue value={intent.drawdownId} label="drawdownId" />,
              },
              {
                label: 'Bre-B (endToEndId)',
                value: <CopyValue value={intent.endToEndId} label="endToEndId" />,
              },
              { label: 'traceId', value: <CopyValue value={detail.traceId} label="traceId" /> },
              { label: 'Código de autorización', value: intent.authCode ?? '—' },
              { label: 'Completada', value: formatDateTime(intent.completedAt) },
            ]}
          />
        </Panel>
      </div>

      <Banners detail={detail} reason={reason} />

      <div className="flex flex-col gap-6">
        <Panel
          title="Timeline de la saga"
          description="Cada intento con su sistema, duración, código externo y reasonCode traducido."
        >
          <SagaTimeline
            saga={detail.saga}
            externalCalls={detail.externalCalls}
            retryableStepIds={retryable}
            onRetry={(step) => {
              setPending({ kind: 'retry', step });
            }}
          />
        </Panel>
        <div className="grid items-start gap-6 lg:grid-cols-[22rem_minmax(0,1fr)]">
          <ReceiptPanel detail={detail} />
          <Panel title="Eventos y webhooks" bodyClassName="p-0">
            <ul className="divide-y divide-border">
              {detail.events.map((event) => (
                <li key={event.id} className="flex items-start justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <code className="block truncate font-mono text-xs font-semibold text-foreground">
                      {event.type}
                    </code>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(event.occurredAt)}
                    </span>
                  </div>
                  <Badge variant={WEBHOOK_TONE[event.webhook.status]}>
                    {WEBHOOK_LABEL[event.webhook.status]}
                    {event.webhook.attempts > 1 && ` ×${event.webhook.attempts}`}
                  </Badge>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      </div>

      <ConfirmActionDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        title={pending ? ACTION_COPY[pending.kind].title : ''}
        description={pending ? ACTION_COPY[pending.kind].description : undefined}
        destructive={pending?.kind === 'force-compensation'}
        confirmLabel={pending ? ACTION_COPY[pending.kind].confirm : 'Confirmar'}
        reasonLabel={
          pending?.kind === 'resolve' ? 'Nota de resolución (queda en auditoría)' : undefined
        }
        summary={
          pending && (
            <SummaryRows
              rows={[
                ['Compra', `${intent.payee.name} · ${detail.customer.fullName}`],
                ['Monto', <MoneyText key="m" value={intent.amount.amount} size="sm" />],
                ['Estado actual', PAYMENT_STATUS[intent.status].label],
                ...(pending.step
                  ? ([
                      [
                        'Paso',
                        `${SAGA_STEP[pending.step.step].label} · intento ${pending.step.attempt + 1}`,
                      ],
                    ] as [ReactNode, ReactNode][])
                  : []),
                ...(pending.kind === 'force-compensation'
                  ? ([['Bre-B', BREB_LABEL[detail.breb.status]]] as [ReactNode, ReactNode][])
                  : []),
              ]}
            />
          )
        }
        onConfirm={async (reasonText) => {
          if (!pending) return;
          await mutation.mutateAsync({
            kind: pending.kind,
            reason: reasonText,
            ...(pending.step && { stepId: pending.step.id }),
          });
        }}
      />
    </>
  );
}

function Banners({
  detail,
  reason,
}: {
  detail: AdminPaymentIntentDetail;
  reason: ReturnType<typeof getReasonCode> | null;
}) {
  const { intent } = detail;
  const items: ReactNode[] = [];
  if (intent.status === 'PENDING_CONFIRMATION') {
    const schedule = PAYMENT_INTENT_TIMINGS.pendingStatusQuerySeconds
      .map((s) => (s < 60 ? `${s} s` : `${s / 60} min`))
      .join(', ');
    items.push(
      <Banner
        key="pending"
        tone="warning"
        icon={<AlertTriangleIcon />}
        title="En verificación: Bre-B no ha dado un estado definitivo"
      >
        El sistema consulta Bre-B a los {schedule} y luego cada 15 min. Último estado consultado:{' '}
        <strong>{BREB_LABEL[detail.breb.status]}</strong>
        {detail.breb.checkedAt && ` (${formatDateTime(detail.breb.checkedAt)})`}. Nunca se compensa
        sin que Bre-B confirme rechazo o inexistencia.
      </Banner>,
    );
  }
  if (intent.status === 'FAILED' && reason && intent.reasonCode) {
    items.push(
      <Banner
        key="failed"
        tone="danger"
        icon={<CircleSlashIcon />}
        title={`${intent.reasonCode} · ${reason.operatorDetail}`}
      >
        Mensaje que vio el cliente: “{formatCustomerMessage(intent.reasonCode)}”
        {detail.saga.some((s) => s.compensation) && ' La saga compensó: el cupo quedó liberado.'}
      </Banner>,
    );
  }
  if (detail.openException) {
    items.push(
      <Banner
        key="exc"
        tone="warning"
        icon={<RotateCcwIcon />}
        title={`Excepción abierta: ${detail.openException.type}`}
      >
        {intent.postingStatus === 'EXCEPTION'
          ? 'Bre-B confirmó el pago al comercio, pero el posteo en Credit Core no terminó. Reintenta el paso de captura (idempotente) o marca la excepción como resuelta con una nota.'
          : 'La compra está atascada sin estado definitivo. Consulta Bre-B y, si confirma que no existe, fuerza la compensación.'}
      </Banner>,
    );
  }
  if (items.length === 0) return null;
  return <div className="mb-6 flex flex-col gap-3">{items}</div>;
}

function Banner({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'warning' | 'danger';
  icon: ReactNode;
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      role="status"
      className={cn(
        'flex gap-3 rounded-2xl border px-5 py-4 text-sm',
        tone === 'warning'
          ? 'border-warning/40 bg-warning-soft text-warning-soft-foreground'
          : 'border-destructive/30 bg-danger-soft text-danger-soft-foreground',
      )}
    >
      <span className="mt-0.5 shrink-0 [&_svg]:size-5">{icon}</span>
      <div>
        <p className="font-semibold">{title}</p>
        <p className="mt-0.5">{children}</p>
      </div>
    </div>
  );
}

function ReceiptPanel({ detail }: { detail: AdminPaymentIntentDetail }) {
  const receipt = detail.intent.receipt;
  return (
    <Panel
      title="Comprobante"
      actions={
        receipt && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              window.print();
            }}
          >
            <PrinterIcon /> Imprimir
          </Button>
        )
      }
    >
      {receipt ? (
        <div className="text-sm">
          <p className="text-xs text-muted-foreground">N.º {receipt.receiptNumber}</p>
          <MoneyText value={receipt.amount.amount} size="lg" className="mt-1 block text-primary" />
          <p className="text-muted-foreground">a {receipt.payee.name}</p>
          <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2">
            <dt className="text-muted-foreground">Cuotas</dt>
            <dd className="text-right font-medium">
              {receipt.installments} ×{' '}
              <MoneyText value={receipt.installmentAmount.amount} size="sm" />
            </dd>
            <dt className="text-muted-foreground">Tasa</dt>
            <dd className="text-right font-medium">{formatRate(receipt.rateEA)}</dd>
            <dt className="text-muted-foreground">Autorización</dt>
            <dd className="text-right font-mono">{receipt.authCode}</dd>
            <dt className="text-muted-foreground">Fecha</dt>
            <dd className="text-right whitespace-nowrap">{formatDateTime(receipt.completedAt)}</dd>
          </dl>
          <p
            className="mt-3 truncate font-mono text-xs text-muted-foreground"
            title={receipt.endToEndId}
          >
            {receipt.endToEndId}
          </p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Se emite cuando la compra queda completada.</p>
      )}
    </Panel>
  );
}

const POSTING_LABEL = {
  NOT_APPLICABLE: 'no aplica',
  POSTED: 'aplicado',
  RETRYING: 'reintentando',
  EXCEPTION: 'con excepción',
} as const;

const BREB_LABEL = {
  UNKNOWN: 'sin consultar',
  PENDING: 'pendiente',
  COMPLETED: 'pago confirmado',
  REJECTED: 'rechazada',
  NOT_FOUND: 'transferencia inexistente',
} as const;

const WEBHOOK_LABEL = {
  DELIVERED: 'Entregado',
  PENDING: 'Pendiente',
  RETRYING: 'Reintentando',
  FAILED: 'Fallido',
  NOT_SUBSCRIBED: 'Sin suscripción',
} as const;
const WEBHOOK_TONE = {
  DELIVERED: 'success',
  PENDING: 'secondary',
  RETRYING: 'warning',
  FAILED: 'danger',
  NOT_SUBSCRIBED: 'outline',
} as const;

const ACTION_COPY: Readonly<
  Record<OperatorActionKind, { title: string; description: string; confirm: string }>
> = {
  retry: {
    title: 'Reintentar paso de la saga',
    description: 'Se repite el paso con la misma llave de idempotencia: no duplica el efecto.',
    confirm: 'Reintentar paso',
  },
  'query-breb': {
    title: 'Consultar estado en Bre-B',
    description:
      'Pide a Bre-B el estado de la transferencia ahora. Si está confirmada, la compra se completa.',
    confirm: 'Consultar ahora',
  },
  'force-compensation': {
    title: 'Forzar compensación',
    description:
      'Libera el cupo y deja la compra fallida. Solo procede porque Bre-B confirmó que el pago no existe o fue rechazado.',
    confirm: 'Forzar compensación',
  },
  resolve: {
    title: 'Marcar excepción como resuelta',
    description: 'Cierra la excepción de conciliación con tu nota. No cambia el dinero ni el cupo.',
    confirm: 'Marcar resuelta',
  },
};
